import { t } from "i18next";
import { observer } from "mobx-react-lite";
import { useCallback, useState } from "react";
import { DotsMenu, Icon, MenuOption, Tooltip } from "@smartsuite/react-ui/lib";
import { Colors, SolutionColorVariables } from "@smartsuite/types";
import { useRootStore } from "../../../stores/hooks";
import { MenuOptionList } from "../../ActionMenu/styles";
import { useFlowCanvasMenuDismiss } from "../../FlowCanvas/hooks/use-flow-canvas-menu-dismiss";
import type { BranchRef } from "../../../stores/automation/branches.store";
import type { BranchValidationIssue } from "../../../stores/automation/utils/inputValidation";
import { getBranchValidationIssue } from "../../../stores/automation/utils/inputValidation";
import { plural } from "../../../locales/i18n";
import { getLocalCssColorVariables } from "../../../utils/cssVariablesUtils";
import { useDeleteBranch } from "./useDeleteBranch";
import {
  StyledBranchHeaderCard,
  StyledBranchHeaderIcon,
  StyledBranchHeaderText,
  StyledBranchHeaderLabel,
  StyledBranchHeaderSummary,
  StyledBranchMenuSlot,
} from "./style";
// Reuse the action cards' warning-icon wrapper so the branch's validation
// triangle sits in the same spot (right: -25px, vertically centered).
import { WarningIcon } from "../../AddTriggerButton/styles";

const BRANCH_ISSUE_MESSAGE_KEY: Record<BranchValidationIssue, string> = {
  "name-empty": "branches.name-required-error",
  "name-duplicate": "branches.name-duplicate-error",
  "conditions-missing": "branches.conditions-required-error",
  "conditions-invalid": "branches.conditions-invalid-error",
  "no-content": "branches.content-required-error",
};

interface BranchHeaderCardProps {
  blockInstanceId: number;
  branchHash: string | "else";
  isSelected: boolean;
  /** Stable across renders (id-bound here, not in the parent) so a memoized
   * parent isn't defeated by a fresh closure per render. */
  onSelect: (instanceId: number, branchHash: string) => void;
  /** Kebab з пунктом Delete рендериться лише коли гілку МОЖНА видалити
   * (спека: остання гілка — контролі сховані). */
  isDeletable: boolean;
  /** Стабільний — після видалення переводить вибір на панель блоку. */
  onBranchDeleted: (instanceId: number) => void;
}

/**
 * The branch header card (icon + label + condition summary + optional delete
 * kebab), extracted from BranchColumn so the React Flow canvas can render the
 * same card as a standalone node without forking the markup. BranchColumn
 * still owns the branch's action list; this component is only the card.
 */
export const BranchHeaderCard = observer<BranchHeaderCardProps>(
  ({
    blockInstanceId,
    branchHash,
    isSelected,
    onSelect,
    isDeletable,
    onBranchDeleted,
  }) => {
    const store = useRootStore();
    const branchesStore = store.automations.candidate.branches;

    const ref: BranchRef = { instanceId: blockInstanceId, branchHash };
    const branch = branchesStore.findBranch(ref);

    const [openDotMenu, setOpenDotMenu] = useState(false);
    useFlowCanvasMenuDismiss(useCallback(() => setOpenDotMenu(false), []));
    const deleteBranchFlow = useDeleteBranch(blockInstanceId, () =>
      onBranchDeleted(blockInstanceId),
    );

    if (branch === undefined) return null;

    // Only after a save attempt (submit), matching how action cards reveal
    // their warning icon -- not while the user is still building the branch.
    const block = branchesStore.findBlock(blockInstanceId);
    const validationIssue =
      store.automations.candidate.isTriggerSubmitted && block !== undefined
        ? getBranchValidationIssue(
            store.automations.candidate,
            block,
            String(branchHash),
          )
        : undefined;

    const summary = branchesStore.conditionSummary(ref);
    const hasConditions = typeof summary === "number";
    const mutedSummaryText =
      summary === "else"
        ? t("branches.if-no-conditions-met")
        : t("branches.no-conditions");

    return (
      <StyledBranchHeaderCard
        data-testid={`branch-header-${String(branchHash)}`}
        data-active={isSelected}
        $isActive={isSelected}
        onClick={() => onSelect(blockInstanceId, String(branchHash))}
      >
        <StyledBranchHeaderIcon>
          {/* checklist is loaded from the MISC bucket (the Figma glyph);
              the default "M" bucket has no such icon and renders blank */}
          <Icon
            name="checklist"
            size="MISC"
            dimension={{ width: 20, height: 20 }}
            color="currentColor"
          />
        </StyledBranchHeaderIcon>
        <StyledBranchHeaderText>
          {branch.label !== "" && (
            <StyledBranchHeaderLabel $hasError={validationIssue !== undefined}>
              {branch.label}
            </StyledBranchHeaderLabel>
          )}
          <StyledBranchHeaderSummary $hasConditions={hasConditions}>
            {hasConditions ? (
              <>
                <span className="branches-condition-count">{summary}</span>{" "}
                {t(plural("branches.condition-unit", summary))}
              </>
            ) : (
              mutedSummaryText
            )}
          </StyledBranchHeaderSummary>
        </StyledBranchHeaderText>
        {validationIssue !== undefined && (
          <WarningIcon>
            <Tooltip
              placements={["top"]}
              text={t(BRANCH_ISSUE_MESSAGE_KEY[validationIssue])}
              target={
                <Icon
                  size="M"
                  name="important-lined"
                  color={Colors.bRed}
                  width={26}
                  height={26}
                  data-testid={`branch-warning-${String(branchHash)}`}
                />
              }
            />
          </WarningIcon>
        )}
        {isDeletable && (
          <StyledBranchMenuSlot>
            <DotsMenu
              data-testid={`branch-menu-${String(branchHash)}`}
              opened={openDotMenu}
              // stopPropagation: кебаб живе всередині клікабельної картки --
              // без нього відкриття меню бабблиться в onClick і перемикає вибір
              onToggle={(open, e) => {
                e?.stopPropagation();
                setOpenDotMenu(open);
              }}
              size="s"
              minWidth={200}
              at="bottom"
              my="top+12"
              smallListPaddings={true}
              backgroundColors={{
                hover: SolutionColorVariables.sc2,
                active: SolutionColorVariables.sc2,
              }}
              colors={{
                default: Colors.dGrey,
                hover: SolutionColorVariables.sc1,
                active: SolutionColorVariables.sc1,
              }}
              portalStyles={getLocalCssColorVariables(store.theme)}
            >
              <MenuOptionList>
                <MenuOption
                  key="delete-branch"
                  dataTestId={`branch-delete-${String(branchHash)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteBranchFlow(branchHash);
                    setOpenDotMenu(false);
                  }}
                  iconName={"trash-1"}
                  iconColor={Colors.bRed}
                  iconSize={"S"}
                  iconSmallMargin
                >
                  {t("branches.delete-branch")}
                </MenuOption>
              </MenuOptionList>
            </DotsMenu>
          </StyledBranchMenuSlot>
        )}
      </StyledBranchHeaderCard>
    );
  },
);
BranchHeaderCard.displayName = "BranchHeaderCard";
